import React, {
  Fragment,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  memo,
} from "react";
import styled from "styled-components";
import { RenderDownload } from "../../../../../others/DocViewer";
import DocumentViewer from "../../../../../others/DocViewer/components/DocumentViewer";
import { Enums_TypeUpload } from "../../../../action/defaultEnums";
import Pending from "../Component/Pending";
import TreeFile from "./TreeFile";
import {
  Box,
  Breadcrumbs,
  CircularProgress,
  Icon,
  IconButton,
  Link,
  Tooltip,
  Typography,
} from "@mui/material";
import Main from "../../../../../service/GateWay/Main";

const Action = styled.div`
  cursor: pointer;
  :hover {
    background-color: rgba(0, 0, 0, 0.04);
  }
`;

const ActionText = styled.div`
  vertical-align: middle !important;
  text-align: left;
`;

/**
 * Component hiển thị danh sách file đính kèm
 * @param {Object} props - Props của component
 * @param {boolean} props.showDocDraw - Hiển thị drawer tài liệu
 * @param {string|number} props.id - ID để lấy danh sách file
 * @param {Object} props.TEndpoint - Endpoint API để lấy file
 * @param {boolean} props.isEdit - Cho phép chỉnh sửa
 * @param {boolean} props.isTree - Hiển thị dạng tree
 * @param {boolean} props.notPopup - Không hiển thị popup
 * @param {Function} props.onClickPDF - Callback khi click PDF
 * @param {string} props.stylePDF - Style cho PDF
 * @param {boolean} props.cardView - Hiển thị dạng card chuyên nghiệp
 * @param {boolean} [props.isDownload=false] - Hiển thị nút tải về cho mỗi file (mọi loại file)
 */
const Index = React.forwardRef(
  (
    {
      showDocDraw,
      id,
      TEndpoint,
      isEdit,
      isTree,
      notPopup,
      onClickPDF,
      stylePDF,
      cardView,
      isDownload = false,
      ...props
    },
    ref,
  ) => {
    const [files, setFiles] = useState([]);
    const [folders, setFolders] = useState([]);
    const [currentPath, setCurrentPath] = useState("");
    const [pathHistory, setPathHistory] = useState([]);
    const [loading, setLoading] = useState(false);
    const [browseLoading, setBrowseLoading] = useState(false);
    const [activeDoc, setActiveDoc] = useState(null);
    const refPDF = useRef();
    const refTreeFile = useRef();

    const transformFileData = useCallback((resultObj) => {
      return resultObj.map((f) => ({
        name: f.fileName,
        moTa: f.moTa,
        label: f.fileName,
        path: f.filePath,
        id: f.etag,
        parentId: f.tags?.type,
      }));
    }, []);

    const getVanBan = useCallback(
      async (fileId, isReturn = false) => {
        if (!fileId || !TEndpoint?.getAllFile) {
          setLoading(false);
          return isReturn ? [] : undefined;
        }
        try {
          setLoading(true);
          const { resultObj } = await TEndpoint.getAllFile(fileId);
          const transformedFiles = transformFileData(resultObj || []);
          if (isReturn) {
            const enumsCopy = Enums_TypeUpload.map((cur) => ({
              ...cur,
              id: cur.value,
            }));
            return [...transformedFiles, ...enumsCopy];
          }
          setFiles(transformedFiles);
        } catch (error) {
          console.error("Lỗi khi lấy danh sách file:", error);
          if (!isReturn) setFiles([]);
        } finally {
          setLoading(false);
        }
      },
      [TEndpoint, transformFileData],
    );

    // Browse attachments (folder + file) for cardView
    const browseAttachments = useCallback(
      async (browsePath = "") => {
        if (!id || !TEndpoint?.BrowseAttachments) return;
        setBrowseLoading(true);
        try {
          const { resultObj } = await TEndpoint.BrowseAttachments(
            id,
            browsePath || undefined,
          );
          const { folders: apiFolders = [], files: apiFiles = [] } =
            resultObj || {};
          setFolders(apiFolders);
          setFiles(
            apiFiles.map((f) => ({
              name: f.name,
              label: f.name,
              path: f.filePath,
              id: f.etag,
              moTa: null,
              parentId: null,
            })),
          );
          setCurrentPath(browsePath);
        } catch {
          // Fallback to getAllFile
          try {
            const { resultObj } = await TEndpoint.getAllFile(id);
            setFolders([]);
            setFiles(transformFileData(resultObj || []));
          } catch {
            setFolders([]);
            setFiles([]);
          }
        } finally {
          setBrowseLoading(false);
        }
      },
      [id, TEndpoint, transformFileData],
    );

    // Folder navigation
    const handleOpenFolder = useCallback(
      (folder) => {
        const newPath = currentPath
          ? `${currentPath}/${folder.name}`
          : folder.relativePath || folder.name;
        setPathHistory((prev) => [
          ...prev,
          { name: folder.name, path: newPath },
        ]);
        browseAttachments(newPath);
      },
      [currentPath, browseAttachments],
    );

    const handleBreadcrumbClick = useCallback(
      (index) => {
        if (index === -1) {
          setPathHistory([]);
          browseAttachments("");
        } else {
          const target = pathHistory[index];
          setPathHistory((prev) => prev.slice(0, index + 1));
          browseAttachments(target.path);
        }
      },
      [pathHistory, browseAttachments],
    );

    useEffect(() => {
      if (isTree || !id) return;
      if (showDocDraw !== undefined && !showDocDraw) return;
      if (cardView && TEndpoint?.BrowseAttachments) {
        setCurrentPath("");
        setPathHistory([]);
        browseAttachments("");
      } else {
        getVanBan(id);
      }
    }, [
      getVanBan,
      browseAttachments,
      id,
      isTree,
      showDocDraw,
      cardView,
      TEndpoint,
    ]);

    /**
     * Expose methods cho parent component thông qua ref
     * Note: files không cần trong dependency vì getData() sẽ luôn trả về giá trị mới nhất thông qua closure
     */
    useImperativeHandle(
      ref,
      () => ({
        getData: () => files,
        reload: (fileId) => {
          if (cardView && TEndpoint?.BrowseAttachments) {
            setCurrentPath("");
            setPathHistory([]);
            browseAttachments("");
          } else {
            getVanBan(fileId);
          }
        },
      }),
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [getVanBan, browseAttachments, cardView, TEndpoint],
    );

    /**
     * Xử lý khi click mở PDF
     * @param {Object} file - File object cần mở
     */
    const handleOpenPDF = useCallback((file) => {
      if (refPDF.current?._openDocument) {
        refPDF.current._openDocument(file);
      }
    }, []);

    /**
     * Xử lý khi click mở file trong cardView mode
     * @param {Object} file - File object cần mở
     */
    const handleOpenFileCard = useCallback(
      (file) => {
        if (onClickPDF) {
          onClickPDF(file);
        } else {
          setActiveDoc(file);
        }
      },
      [onClickPDF],
    );

    /**
     * Đóng DocumentViewer
     */
    const handleCloseViewer = useCallback(() => {
      setActiveDoc(null);
    }, []);

    /**
     * Render danh sách file dạng list
     * Note: props không cần trong dependency vì nó là object và sẽ thay đổi reference mỗi lần render,
     * gây re-render vô hạn nếu thêm vào dependency
     */
    const renderFileList = useMemo(() => {
      if (!files || files.length === 0) {
        return <div>Không có tài liệu</div>;
      }

      return files.map((file) => (
        <Action
          key={file.id || `file-${file.path}`}
          onClick={() => handleOpenPDF(file)}
          className="d-flex justify-content-start align-items-center p-1"
        >
          <ActionText style={{ flex: 1, minWidth: 0 }}>
            <RenderDownload
              stylePDF={stylePDF}
              ref={refPDF}
              onClickPDF={onClickPDF}
              file={file}
              isEdit={isEdit}
              {...props}
            />
          </ActionText>
          {isDownload && file.path && (
            <Tooltip title="Tải về" arrow>
              <IconButton
                size="small"
                component="a"
                href={Main.getDocUrlByPath(file.path)}
                download={file.name || file.label}
                onClick={(e) => e.stopPropagation()}
                sx={{ color: "#16a34a", flexShrink: 0 }}
              >
                <Icon sx={{ fontSize: 18 }}>download</Icon>
              </IconButton>
            </Tooltip>
          )}
        </Action>
      ));
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [files, handleOpenPDF, stylePDF, onClickPDF, isEdit, isDownload]);

    /**
     * Lấy icon và màu theo loại file
     * @param {string} fileName - Tên file
     * @returns {{ icon: string, color: string, bgColor: string }}
     */
    const getFileTypeInfo = useCallback((fileName) => {
      const ext = fileName?.split(".").pop()?.toLowerCase() || "";
      const fileTypes = {
        pdf: { icon: "📄", color: "#dc2626", bgColor: "#fef2f2", label: "PDF" },
        doc: { icon: "📝", color: "#2563eb", bgColor: "#eff6ff", label: "DOC" },
        docx: {
          icon: "📝",
          color: "#2563eb",
          bgColor: "#eff6ff",
          label: "DOCX",
        },
        xls: { icon: "📊", color: "#16a34a", bgColor: "#f0fdf4", label: "XLS" },
        xlsx: {
          icon: "📊",
          color: "#16a34a",
          bgColor: "#f0fdf4",
          label: "XLSX",
        },
        ppt: { icon: "📽️", color: "#ea580c", bgColor: "#fff7ed", label: "PPT" },
        pptx: {
          icon: "📽️",
          color: "#ea580c",
          bgColor: "#fff7ed",
          label: "PPTX",
        },
        jpg: { icon: "🖼️", color: "#7c3aed", bgColor: "#f5f3ff", label: "IMG" },
        jpeg: {
          icon: "🖼️",
          color: "#7c3aed",
          bgColor: "#f5f3ff",
          label: "IMG",
        },
        png: { icon: "🖼️", color: "#7c3aed", bgColor: "#f5f3ff", label: "IMG" },
        zip: { icon: "📦", color: "#ca8a04", bgColor: "#fefce8", label: "ZIP" },
        rar: { icon: "📦", color: "#ca8a04", bgColor: "#fefce8", label: "RAR" },
      };
      return (
        fileTypes[ext] || {
          icon: "📎",
          color: "#6b7280",
          bgColor: "#f3f4f6",
          label: "FILE",
        }
      );
    }, []);

    /**
     * Render danh sách file dạng card chuyên nghiệp
     */
    const renderFileCards = useMemo(() => {
      const totalItems = folders.length + files.length;
      const isInSubfolder = pathHistory.length > 0;

      if (browseLoading) {
        return (
          <Box sx={{ textAlign: "center", py: 3 }}>
            <CircularProgress size={24} />
            <Typography sx={{ fontSize: "12px", color: "#9ca3af", mt: 1 }}>
              Đang tải...
            </Typography>
          </Box>
        );
      }

      return (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
          {/* Breadcrumb */}
          {isInSubfolder && (
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 0.5, mb: 0.5 }}
            >
              <Tooltip title="Quay về thư mục gốc" arrow>
                <IconButton
                  size="small"
                  onClick={() => handleBreadcrumbClick(-1)}
                  sx={{ p: 0.25 }}
                >
                  <Icon sx={{ fontSize: 16, color: "#64748b" }}>home</Icon>
                </IconButton>
              </Tooltip>
              <Breadcrumbs
                separator={
                  <Icon sx={{ fontSize: 12, color: "#94a3b8" }}>
                    chevron_right
                  </Icon>
                }
                sx={{
                  "& .MuiBreadcrumbs-li": { lineHeight: 1 },
                  fontSize: "0.75rem",
                }}
              >
                <Link
                  component="button"
                  underline="hover"
                  color="text.secondary"
                  onClick={() => handleBreadcrumbClick(-1)}
                  sx={{
                    fontSize: "0.75rem",
                    cursor: "pointer",
                    fontWeight: 500,
                  }}
                >
                  Gốc
                </Link>
                {pathHistory.map((segment, idx) => {
                  const isLast = idx === pathHistory.length - 1;
                  return isLast ? (
                    <Typography
                      key={idx}
                      sx={{
                        fontSize: "0.75rem",
                        fontWeight: 600,
                        color: "#334155",
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                      }}
                    >
                      <Icon sx={{ fontSize: 12, color: "#f59e0b" }}>
                        folder
                      </Icon>
                      {segment.name}
                    </Typography>
                  ) : (
                    <Link
                      key={idx}
                      component="button"
                      underline="hover"
                      color="text.secondary"
                      onClick={() => handleBreadcrumbClick(idx)}
                      sx={{ fontSize: "0.75rem", cursor: "pointer" }}
                    >
                      {segment.name}
                    </Link>
                  );
                })}
              </Breadcrumbs>
            </Box>
          )}

          {totalItems === 0 ? (
            <Box
              sx={{
                textAlign: "center",
                py: 3,
                color: "#9ca3af",
                fontSize: "13px",
              }}
            >
              {isInSubfolder ? "Thư mục trống" : "Không có tài liệu"}
            </Box>
          ) : (
            <>
              {/* Folders */}
              {folders.map((folder, index) => (
                <Box
                  key={`folder-${index}-${folder.name}`}
                  onClick={() => handleOpenFolder(folder)}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1.5,
                    padding: "4px 12px",
                    backgroundColor: "#fffbeb",
                    borderRadius: "8px",
                    border: "1px solid #fde68a",
                    cursor: "pointer",
                    userSelect: "none",
                    transition: "all 0.2s ease",
                    "&:hover": {
                      backgroundColor: "#fef3c7",
                      borderColor: "#f59e0b",
                      boxShadow: "0 2px 8px rgba(245, 158, 11, 0.15)",
                      transform: "translateY(-1px)",
                    },
                  }}
                >
                  <Box
                    sx={{
                      width: 40,
                      height: 40,
                      borderRadius: "8px",
                      backgroundColor: "#fef3c7",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}
                  >
                    <Typography sx={{ fontSize: "18px" }}>📁</Typography>
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      sx={{
                        fontSize: "13px",
                        fontWeight: 600,
                        color: "#92400e",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                      }}
                    >
                      {folder.name}
                    </Typography>
                    <Typography sx={{ fontSize: "11px", color: "#b45309" }}>
                      Chọn để mở
                    </Typography>
                  </Box>
                  <Icon sx={{ color: "#d97706", fontSize: 16, opacity: 0.6 }}>
                    chevron_right
                  </Icon>
                </Box>
              ))}
              {/* Files */}
              {files.map((file) => {
                const fileInfo = getFileTypeInfo(file.name);
                return (
                  <Box
                    key={file.id || `file-${file.path}`}
                    onClick={() => handleOpenFileCard(file)}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      padding: "4px 12px",
                      backgroundColor: "#ffffff",
                      borderRadius: "8px",
                      border: "1px solid #e5e7eb",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: "#f8fafc",
                        borderColor: "#3b82f6",
                        boxShadow: "0 2px 8px rgba(59, 130, 246, 0.15)",
                        transform: "translateY(-1px)",
                      },
                    }}
                  >
                    <Box
                      sx={{
                        width: 40,
                        height: 40,
                        borderRadius: "8px",
                        backgroundColor: fileInfo.bgColor,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}
                    >
                      <Typography sx={{ fontSize: "18px" }}>
                        {fileInfo.icon}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: 500,
                          color: "#1f2937",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {file.name || file.label}
                      </Typography>
                      {file.moTa && (
                        <Typography
                          sx={{
                            fontSize: "11px",
                            color: "#6b7280",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                          }}
                        >
                          {file.moTa}
                        </Typography>
                      )}
                    </Box>
                    {isDownload && file.path && (
                      <Tooltip title="Tải về" arrow>
                        <IconButton
                          size="small"
                          component="a"
                          href={Main.getDocUrlByPath(file.path)}
                          download={file.name || file.label}
                          onClick={(e) => e.stopPropagation()}
                          sx={{ color: "#16a34a", p: 0.5, flexShrink: 0 }}
                        >
                          <Icon sx={{ fontSize: 18 }}>download</Icon>
                        </IconButton>
                      </Tooltip>
                    )}
                    <Box
                      sx={{
                        fontSize: "10px",
                        fontWeight: 600,
                        color: fileInfo.color,
                        backgroundColor: fileInfo.bgColor,
                        padding: "2px 6px",
                        borderRadius: "4px",
                        flexShrink: 0,
                      }}
                    >
                      {fileInfo.label}
                    </Box>
                  </Box>
                );
              })}
            </>
          )}
        </Box>
      );
    }, [
      files,
      folders,
      pathHistory,
      browseLoading,
      handleOpenFileCard,
      handleOpenFolder,
      handleBreadcrumbClick,
      getFileTypeInfo,
      isDownload,
    ]);

    /**
     * Memoize manipulation object để tránh re-render
     */
    const manipulation = useMemo(() => ({ view: getVanBan }), [getVanBan]);

    /**
     * Render component chính
     * Note: props không cần trong dependency vì nó là object và sẽ thay đổi reference mỗi lần render,
     * gây re-render vô hạn nếu thêm vào dependency
     */
    const renderFile = useMemo(() => {
      if (isTree) {
        return (
          <TreeFile
            ref={refTreeFile}
            onClickPDF={onClickPDF}
            manipulation={manipulation}
            id={id}
            notPopup={notPopup}
            {...props}
          />
        );
      }

      return (
        <Pending stylePDF={stylePDF} loading={loading}>
          {cardView ? renderFileCards : renderFileList}
        </Pending>
      );
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
      isTree,
      onClickPDF,
      manipulation,
      id,
      notPopup,
      stylePDF,
      loading,
      renderFileList,
      cardView,
      renderFileCards,
    ]);

    return (
      <Fragment>
        {renderFile}
        {/* DocumentViewer for cardView mode */}
        {cardView && activeDoc && (
          <DocumentViewer
            isEdit={isEdit}
            isModal={true}
            activeDoc={activeDoc}
            onClose={handleCloseViewer}
            {...props}
          />
        )}
      </Fragment>
    );
  },
);

Index.displayName = "FormAttachFile";

export default memo(Index);
